import React, { useState, useEffect } from "react";
import { API_URL } from "../config";

export default function UpsellManager({ 
  companyId, 
  mainForecast, 
  isEdit, 
  isDarkMode, 
  currentUserId
}) {
  const [upsells, setUpsells] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadUpsells();
    }
  }, [companyId]);

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    if (typeof isoDate === 'string' && isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return isoDate;
    }
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return isoDate;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return isoDate || "";
    }
  };

  const loadUpsells = async () => {
    try {
      setIsLoading(true);
      // API_URL už obsahuje /api, tak používáme jen /companies/:id/upsells
      const response = await fetch(`${API_URL}/companies/${companyId}/upsells`);
      if (response.ok) {
        const data = await response.json();
        setUpsells(data);
      }
    } catch (error) {
      console.error("Chyba při načítání upsellů:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUpsell = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setIsLoading(true);
      // API_URL už obsahuje /api
      const response = await fetch(`${API_URL}/companies/${companyId}/upsells`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          forecast: "0",
          bmsl: "",
          closedDate: "",
          contractLength: "",
          createdBy: currentUserId
        })
      });

      if (response.ok) {
        const newUpsell = await response.json();
        setUpsells([newUpsell, ...upsells]);
      } else {
        const error = await response.json();
        alert(error.message || "Chyba při vytváření upsellu");
      }
    } catch (error) {
      console.error("Chyba při vytváření upsellu:", error);
      alert("Chyba při vytváření upsellu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUpsell = async (upsellId, field, value) => {
    try {
      const upsell = upsells.find(u => u._id === upsellId);
      if (!upsell) return;

      const updatedUpsells = upsells.map(u => 
        u._id === upsellId ? { ...u, [field]: value } : u
      );
      setUpsells(updatedUpsells);

      const updatedUpsell = { ...upsell, [field]: value };
      // API_URL už obsahuje /api
      const response = await fetch(`${API_URL}/upsells/${upsellId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          forecast: updatedUpsell.forecast,
          bmsl: updatedUpsell.bmsl,
          closedDate: updatedUpsell.closedDate,
          contractLength: updatedUpsell.contractLength
        })
      });

      if (!response.ok) {
        await loadUpsells();
        alert("Chyba při ukládání upsellu");
      }
    } catch (error) {
      console.error("Chyba při aktualizaci upsellu:", error);
      await loadUpsells();
    }
  };

  const handleDeleteUpsell = async (e, upsellId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm("Opravdu chcete smazat tento upsell?")) return;

    try {
      setIsLoading(true);
      // API_URL už obsahuje /api
      const response = await fetch(`${API_URL}/upsells/${upsellId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        setUpsells(upsells.filter(u => u._id !== upsellId));
      } else {
        alert("Chyba při mazání upsellu");
      }
    } catch (error) {
      console.error("Chyba při mazání upsellu:", error);
      alert("Chyba při mazání upsellu");
    } finally {
      setIsLoading(false);
    }
  };

  if (mainForecast !== "100") {
    return null;
  }

  return (
    <div>
      {/* Skryté tlačítko pro vytvoření upsellu - ovládá se z CompanyForm */}
      <button
        type="button"
        id="upsell-create-btn"
        onClick={handleCreateUpsell}
        disabled={isLoading}
        className="hidden"
      />

      {/* Upselly ve stejném stylu jako hlavní pole - na celou šířku */}
      {upsells.map((upsell, index) => (
        <div key={upsell._id} className="mb-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Forecast s tlačítkem pro smazání */}
            <div className="relative">
              <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm flex items-center justify-between">
                <span>Forecast (Upsell #{index + 1})</span>
                {isEdit && (
                  <button
                    onClick={(e) => handleDeleteUpsell(e, upsell._id)}
                    className="px-2 py-0.5 text-red-500 hover:text-white hover:bg-red-500 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white text-base leading-none border border-red-300 dark:border-red-600 rounded-lg transition-all"
                    title="Smazat upsell"
                  >
                    ×
                  </button>
                )}
              </label>
              <select
                className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                value={upsell.forecast || "0"}
                onChange={(e) => handleUpdateUpsell(upsell._id, "forecast", e.target.value)}
                disabled={!isEdit}
              >
                <option value="0">0%</option>
                <option value="25">25%</option>
                <option value="50">50%</option>
                <option value="75">75%</option>
                <option value="99">99%</option>
                <option value="100">100%</option>
              </select>
            </div>

            {/* BMSL */}
            <div>
              <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">BMSL navýšeno o</label>
              <input
                type="number"
                min="0"
                className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                placeholder="BMSL navýšeno o"
                value={upsell.bmsl || ""}
                onChange={(e) => handleUpdateUpsell(upsell._id, "bmsl", e.target.value)}
                disabled={!isEdit}
              />
            </div>

            {/* Zobchodováno */}
            <div>
              <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Zobchodováno</label>
              <input
                type="date"
                className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                value={formatDateForInput(upsell.closedDate)}
                onChange={(e) => handleUpdateUpsell(upsell._id, "closedDate", e.target.value)}
                disabled={!isEdit || upsell.forecast !== "100"}
              />
            </div>

            {/* Délka smlouvy */}
            <div>
              <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Délka smlouvy</label>
              <select
                className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                value={upsell.contractLength || ""}
                onChange={(e) => handleUpdateUpsell(upsell._id, "contractLength", e.target.value)}
                disabled={!isEdit || upsell.forecast !== "100"}
              >
                <option value="">Vyberte</option>
                <option value="12">12 měsíců</option>
                <option value="24">24 měsíců</option>
                <option value="36">36 měsíců</option>
                <option value="48">48 měsíců</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
